import { useMemo } from 'react'
import {
  startOfDay,
  endOfDay,
  subDays,
  subHours,
  startOfWeek,
  startOfMonth,
  format,
  eachDayOfInterval,
  eachWeekOfInterval,
} from 'date-fns'
import type { DatePeriod } from '@/components/dashboard/filters'
import type { Grouping } from '@/components/dashboard/filters'

export interface DateRange {
  startDate: Date
  endDate: Date
}

export interface DateFilterResult {
  // Current period
  startDate: Date
  endDate: Date
  // Previous period for comparison
  previousStartDate: Date
  previousEndDate: Date
  // Formatted strings for API queries
  startDateISO: string
  endDateISO: string
  previousStartDateISO: string
  previousEndDateISO: string
  // Labels for each date in the period (for chart x-axis)
  dateLabels: string[]
  // Number of days in period
  periodDays: number
}

/**
 * Calculate date ranges based on period selection
 */
export function useDateFilter(
  period: DatePeriod,
  grouping: Grouping = 'daily'
): DateFilterResult {
  return useMemo(() => {
    const now = new Date()
    let startDate: Date
    let endDate: Date = endOfDay(now)

    switch (period) {
      case 'today':
        startDate = startOfDay(now)
        break

      case 'yesterday':
        startDate = startOfDay(subDays(now, 1))
        endDate = endOfDay(subDays(now, 1))
        break

      case 'last24h':
        startDate = subHours(now, 24)
        break

      case 'last7d':
        startDate = startOfDay(subDays(now, 6))
        break

      case 'last30d':
        startDate = startOfDay(subDays(now, 29))
        break

      case 'weekToDate':
        startDate = startOfWeek(now, { weekStartsOn: 1 }) // Monday
        break

      case 'monthToDate':
        startDate = startOfMonth(now)
        break

      default:
        startDate = startOfDay(subDays(now, 29))
    }

    // Calculate period length in days
    const periodMs = endDate.getTime() - startDate.getTime()
    const periodDays = Math.ceil(periodMs / (1000 * 60 * 60 * 24))

    // Calculate previous period (same length, immediately before)
    const previousEndDate = new Date(startDate.getTime() - 1)
    const previousStartDate = new Date(previousEndDate.getTime() - periodMs)

    // Generate date labels for chart
    let dateLabels: string[]
    if (grouping === 'weekly') {
      const weeks = eachWeekOfInterval(
        { start: startDate, end: endDate },
        { weekStartsOn: 1 }
      )
      dateLabels = weeks.map((week) => format(week, 'yyyy-MM-dd'))
    } else {
      const days = eachDayOfInterval({ start: startDate, end: endDate })
      dateLabels = days.map((day) => format(day, 'yyyy-MM-dd'))
    }

    return {
      startDate,
      endDate,
      previousStartDate,
      previousEndDate,
      startDateISO: startDate.toISOString(),
      endDateISO: endDate.toISOString(),
      previousStartDateISO: previousStartDate.toISOString(),
      previousEndDateISO: previousEndDate.toISOString(),
      dateLabels,
      periodDays,
    }
  }, [period, grouping])
}

/**
 * Get a human-readable label for the period
 */
export function getPeriodLabel(period: DatePeriod): string {
  switch (period) {
    case 'today':
      return 'Today'
    case 'yesterday':
      return 'Yesterday'
    case 'last24h':
      return 'Last 24 hours'
    case 'last7d':
      return 'Last 7 days'
    case 'last30d':
      return 'Last 30 days'
    case 'weekToDate':
      return 'Week to date'
    case 'monthToDate':
      return 'Month to date'
    default:
      return 'Custom'
  }
}

/**
 * Get comparison label
 */
export function getComparisonLabel(period: DatePeriod): string {
  switch (period) {
    case 'today':
      return 'vs yesterday'
    case 'yesterday':
      return 'vs day before'
    case 'last24h':
      return 'vs previous 24h'
    case 'last7d':
      return 'vs previous 7 days'
    case 'last30d':
      return 'vs previous 30 days'
    case 'weekToDate':
      return 'vs last week'
    case 'monthToDate':
      return 'vs last month'
    default:
      return 'vs previous period'
  }
}
